// @flow

import React, { useCallback, useEffect, useState } from 'react';
import styled, { type StyledComponent } from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { __ } from '../../../i18n/l10n';
import dompurify from 'dompurify';

import type {
  InternationalPhone,
  ListInternationalCountryCallingCodesResponse,
  RootState,
} from '../../../store/types';
import { WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST } from '../../../store/actions';
import { API } from '../../../utils/APIUtils';
import { sizes } from '../../../styles/GlobalStyle';
import { ListItem } from './ListItem';
import { InputText } from './InputText';

type CountryCodesRawProps = {
  className?: string,
  countryCodeChanged: Function,
};

const CountryCodesRaw = ({ className, countryCodeChanged }: CountryCodesRawProps) => {
  const sanitizer = dompurify.sanitize;
  const userRegistration = useSelector((state: RootState) => state.userRegistration);
  const i18n = useSelector((state: RootState) => state.i18n);
  const [search, setSearch] = useState<string>('');
  const [filteredInternationalPhones, setFilteredInternationalPhones] = useState<
    InternationalPhone[]
  >([]);

  const initialState: ListInternationalCountryCallingCodesResponse = { countries: [] };
  const [internationalPhones, setInternationalPhones] = useState(initialState);

  const dispatch = useDispatch();

  useEffect(() => {
    dispatch({
      type: WS_LIST_INTERNATIONAL_COUNTRY_CALLING_CODES_REQUEST,
      payload: {
        method: API.ListInternationalCountryCallingCodes,
        params: {
          lang: i18n.language,
        },
      },
    });
  }, [dispatch, i18n.language]);

  const filterPhoneCodes = (phones) => {
    return phones.filter((phone) => {
      return (
        phone.code &&
        (phone.label.toLowerCase().indexOf(search.toLowerCase()) !== -1 ||
          `+${phone.code ? phone.code.toString() : ''}`.indexOf(search) !== -1)
      );
    });
  };

  useEffect(() => {
    if (userRegistration.listInternationalCountryCallingCodes.response) {
      const internationalPhones = userRegistration.listInternationalCountryCallingCodes.response;
      const filteredCountries = filterPhoneCodes(internationalPhones.countries);
      const filteredPhones = {
        countries: filteredCountries,
      };

      setInternationalPhones(filteredPhones);
    }
  }, [userRegistration.listInternationalCountryCallingCodes.response]);

  useEffect(() => {
    if (search !== '' && internationalPhones) {
      const allPhones = internationalPhones.countries.sort((a, b) => (a.label < b.label ? -1 : 1));
      const filteredPhones = filterPhoneCodes(allPhones);

      setFilteredInternationalPhones(filteredPhones);
    }
  }, [internationalPhones, search]);

  const renderResultLabel = (phone: InternationalPhone) => {
    let label = phone.code ? `${phone.label} +${phone.code}` : `${phone.label}`;
    label = label
      .toLowerCase()
      .replace(search.toLowerCase(), `<strong>${search.toLowerCase()}</strong>`);

    return label;
  };

  const sortByLabel = useCallback((a, b) => {
    if (typeof a.label === 'string' && typeof b.label === 'string') {
      return a.label.localeCompare(b.label);
    }
    if (a.label < b.label) {
      return -1;
    }
    if (a.label > b.label) {
      return 1;
    }
    return 0;
  }, []);

  const renderResultItem = (phone: InternationalPhone) => (
    <li key={phone.label}>
      <ListItem
        value={phone.code ? phone.code.toString() : ''}
        theme={{ filtered: search !== '' }}
        onClick={() => {
          countryCodeChanged([phone.code, phone.alpha2Code]);
        }}
      >
        <span
          dangerouslySetInnerHTML={{
            __html: sanitizer(renderResultLabel(phone)),
          }}
        />
      </ListItem>
    </li>
  );

  const renderDefaultResults = () => (
    <>
      <p>{__('All countries')}</p>
      <ul className="list">
        {internationalPhones.countries.sort(sortByLabel).map(renderResultItem)}
      </ul>
    </>
  );

  const renderFilteredResults = () => (
    <ul className="list">{filteredInternationalPhones.sort(sortByLabel).map(renderResultItem)}</ul>
  );

  const renderResults = () => (search !== '' ? renderFilteredResults() : renderDefaultResults());

  return (
    <div className={className}>
      <p>{__('Choose your country code')}</p>
      <InputText
        value=""
        name="phonePrefix"
        id="phonePrefix"
        label={__('Search')}
        onChange={setSearch}
      />
      {renderResults()}
    </div>
  );
};

export const CountryCodes: StyledComponent<
  CountryCodesRawProps,
  {},
  typeof CountryCodesRaw
> = styled(CountryCodesRaw)`
  .list {
    margin: ${sizes.padding} 0;
  }
`;
